/**
 * Hook centralizado para carregar regras de salvamento
 *
 * Encapsula fetchSavingRules com cache via useApiCache.
 * Evita requisições duplicadas.
 */

import { useMemo } from "react";
import { useApiCache } from "../../../lib/useApiCache";
import type { SavingRule } from "../../../types/quotation";

const SAVING_RULES_ENDPOINT = "/regras-salvamento";
const CACHE_KEY = "saving-rules";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

export interface UseSavingRulesResult {
	/** Regras de salvamento */
	rules: SavingRule[];
	/** Indica se está carregando */
	loading: boolean;
	/** Mensagem de erro (null se não houver erro) */
	error: string | null;
	/** Código do erro para tratamento específico */
	errorCode: "network" | "validation" | "auth" | "unknown" | null;
	/** Força refresh dos dados (ignora cache) */
	refresh: () => Promise<void>;
	/** Indica se já tentou carregar pelo menos uma vez */
	hasAttempted: boolean;
}

/**
 * Hook para carregar regras de salvamento da API
 *
 * @example
 * ```tsx
 * const { rules, loading, error } = useSavingRules();
 *
 * // Encontrar regra para um produto específico
 * const rule = rules.find(r => r.pro_id === proId);
 * ```
 */
export function useSavingRules(): UseSavingRulesResult {
	const { data, loading, error, errorCode, refresh, hasAttempted } =
		useApiCache<SavingRule[]>(CACHE_KEY, SAVING_RULES_ENDPOINT, {
			ttlMs: CACHE_TTL_MS,
			validate: (data) => {
				// A API pode retornar { success: true, data: [...] } ou array direto
				const rules = Array.isArray(data) ? data : data?.data;
				return Array.isArray(rules);
			},
		});

	// ✅ CORRIGIDO: Normalizar dados garantindo sempre array
	const normalizedRules = useMemo(() => {
		if (!data) return [];
		if (Array.isArray(data)) return data;
		if (data?.data && Array.isArray(data.data)) return data.data;
		return [];
	}, [data]);

	return {
		rules: normalizedRules,
		loading,
		error,
		errorCode,
		refresh,
		hasAttempted,
	};
}
